// What the ops phone's warehouse screens read (05 §5.2): the trips and containers at my branch,
// what is expected on one of them, and the boxes and unpaid shipments the phone needs in memory to
// warn about a refusal before the server says so. Everything here is written by the server;
// the phone changes it only through commands (02 §2.7 rule 1). Every query has a scope `where`
// and a `limit` (02 §2.3). None of them needs a composite index: equality and `in` filters only.
import {
  collection,
  doc,
  getDocFromCache,
  limit,
  query,
  where,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore';
import { ulid } from 'ulid';
import {
  activeHolds,
  applyBoxEvent,
  formatMoney,
  formatPrefixedId,
  isCurrencyCode,
  money,
  type Box,
  type BoxEvent,
  type BoxRefusalReason,
} from '@ecobox/domain';
import type { carrierItemDocSchema, containerDocSchema, tripDocSchema } from '@ecobox/schema';
import type { BoxRow, ShipmentRow } from './counter';
import { toDoc, watchOne, watchRows, type Unsubscribe } from './docs';

export type OpsTrip = (typeof tripDocSchema)['_output'];
export type OpsContainer = (typeof containerDocSchema)['_output'];
export type OpsCarrierItem = (typeof carrierItemDocSchema)['_output'];

export type CarrierKind = 'trips' | 'containers';

/** Trips a branch can still load onto. */
export const OPS_LOADING_TRIP_STATUSES = ['loading'] as const;
/** Trips on the road to a branch, or being scanned in there. */
export const OPS_INBOUND_TRIP_STATUSES = ['departed', 'receiving'] as const;
/** Containers still at the export hub: planned, being loaded, or sealed and waiting for the port. */
export const OPS_EXPORT_CONTAINER_STATUSES = ['planned', 'loading', 'closed'] as const;
/** Containers on their way to, or at, the import hub, up to the empty return. */
export const OPS_IMPORT_CONTAINER_STATUSES = [
  'departed',
  'arrived_port',
  'pulled_out',
  'receiving',
  'devanned',
] as const;

export const OPS_CARRIER_LIMIT = 100;
export const OPS_ITEMS_LIMIT = 600;
export const OPS_BOXES_LIMIT = 500;
export const OPS_UNPAID_LIMIT = 500;

const row =
  <T>(idField: string) =>
  (id: string, data: DocumentData): T =>
    toDoc<T>(id, data, idField);

/** A new prefixed id such as `trp_01JA…`, made on the device so a command can name it offline. */
export function newOpsId(prefix: 'trp' | 'exc'): string {
  return formatPrefixedId(prefix, ulid());
}

// ---- trips and containers ----------------------------------------------------------

/** Trips leaving this branch that are still being loaded. */
export function watchOpsLoadingTrips(
  db: Firestore,
  branchId: string,
  onData: (rows: OpsTrip[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'trips'),
    where('fromBranchId', '==', branchId),
    where('status', 'in', [...OPS_LOADING_TRIP_STATUSES]),
    limit(OPS_CARRIER_LIMIT),
  );
  return watchRows(q, row<OpsTrip>('id'), onData, onError);
}

/** Trips on their way to this branch, or being received here. */
export function watchOpsInboundTrips(
  db: Firestore,
  branchId: string,
  onData: (rows: OpsTrip[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'trips'),
    where('toBranchId', '==', branchId),
    where('status', 'in', [...OPS_INBOUND_TRIP_STATUSES]),
    limit(OPS_CARRIER_LIMIT),
  );
  return watchRows(q, row<OpsTrip>('id'), onData, onError);
}

/** Containers at this branch's export side (plan, load, gate in). */
export function watchOpsExportContainers(
  db: Firestore,
  branchId: string,
  onData: (rows: OpsContainer[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'containers'),
    where('exportHubId', '==', branchId),
    where('status', 'in', [...OPS_EXPORT_CONTAINER_STATUSES]),
    limit(OPS_CARRIER_LIMIT),
  );
  return watchRows(q, row<OpsContainer>('id'), onData, onError);
}

/** Containers coming to, or devanning at, this branch as the import hub. */
export function watchOpsImportContainers(
  db: Firestore,
  branchId: string,
  onData: (rows: OpsContainer[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'containers'),
    where('importHubId', '==', branchId),
    where('status', 'in', [...OPS_IMPORT_CONTAINER_STATUSES]),
    limit(OPS_CARRIER_LIMIT),
  );
  return watchRows(q, row<OpsContainer>('id'), onData, onError);
}

/** One trip, live: its counts and state. */
export function watchOpsTrip(
  db: Firestore,
  tripId: string,
  onData: (row: OpsTrip | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'trips', tripId), row<OpsTrip>('id'), onData, onError);
}

/** One container, live. */
export function watchOpsContainer(
  db: Firestore,
  containerId: string,
  onData: (row: OpsContainer | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'containers', containerId), row<OpsContainer>('id'), onData, onError);
}

/**
 * What is expected on one trip or in one container, and what has been scanned off so far.
 * A subcollection is scoped by its parent, which the screen already read. At most 600.
 */
export function watchOpsCarrierItems(
  db: Firestore,
  args: { kind: CarrierKind; id: string },
  onData: (rows: OpsCarrierItem[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, args.kind, args.id, 'items'), limit(OPS_ITEMS_LIMIT));
  return watchRows(q, row<OpsCarrierItem>('barcode'), onData, onError);
}

// ---- where a trip can go ---------------------------------------------------------------

export interface OpsBranch {
  code: string;
  name: string;
  sectorId: string;
}

/** The active branches, for "which branch is this trip going to". Staff may read all of them. */
export function watchOpsBranches(
  db: Firestore,
  onData: (rows: OpsBranch[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, 'branches'), where('active', '==', true), limit(200));
  return watchRows(
    q,
    (id, data): OpsBranch => ({
      code: id,
      name: typeof data.name === 'string' ? data.name : id,
      sectorId: typeof data.sectorId === 'string' ? data.sectorId : '',
    }),
    (rows) => onData(rows.sort((a, b) => a.code.localeCompare(b.code))),
    onError,
  );
}

export interface OpsVehicle {
  id: string;
  plate: string;
  type: string;
}

/**
 * The vehicles set up for this branch (Settings → Branch settings & vehicles). `openTrip` accepts
 * only a vehicle that exists and is switched on (`vehicle_unknown`), so the phone offers these
 * instead of a free-text plate. Staff may read all of them; an equality filter and a limit.
 */
export function watchOpsVehicles(
  db: Firestore,
  branchId: string,
  onData: (rows: OpsVehicle[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, 'vehicles'), where('branchId', '==', branchId), limit(50));
  return watchRows(
    q,
    (id, data): OpsVehicle | null =>
      data.active === false
        ? null
        : {
            id,
            plate: typeof data.plate === 'string' ? data.plate : id,
            type: typeof data.type === 'string' ? data.type : 'other',
          },
    (rows) => onData(rows.sort((a, b) => a.plate.localeCompare(b.plate))),
    onError,
  );
}

// ---- what the phone keeps in memory to warn early ------------------------------------

/**
 * Boxes at this branch, at most 500. Together with `watchOpsUnpaidShipments` they let the load
 * screens warn at once, offline too, because Firestore keeps both in its local cache.
 */
export function watchOpsBoxesHere(
  db: Firestore,
  branchId: string,
  onData: (rows: BoxRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'boxes'),
    // Where the box is now, not where it was collected: a hub also holds what satellites sent it.
    where('currentBranchId', '==', branchId),
    where('status', '==', 'at_branch'),
    limit(OPS_BOXES_LIMIT),
  );
  return watchRows(q, row<BoxRow>('barcode'), onData, onError);
}

/** Shipments at this branch that are unpaid or part paid: their boxes will be refused (D-16). */
export function watchOpsUnpaidShipments(
  db: Firestore,
  branchId: string,
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    where('branchId', '==', branchId),
    where('money.status', 'in', ['unpaid', 'partial']),
    limit(OPS_UNPAID_LIMIT),
  );
  return watchRows(q, row<ShipmentRow>('trackingNo'), onData, onError);
}

/** A box from Firestore's local cache only (instant, works offline), or null when never read. */
export async function readCachedBox(db: Firestore, barcode: string): Promise<BoxRow | null> {
  try {
    const snap = await getDocFromCache(doc(db, 'boxes', barcode));
    return snap.exists() ? toDoc<BoxRow>(snap.id, snap.data(), 'barcode') : null;
  } catch {
    // Not in the cache: the server will decide.
    return null;
  }
}

// ---- the warning before the server's answer ---------------------------------------------

export type PrecheckLevel =
  /** Looks fine, or nothing is known about the box: the server decides. */
  | 'ok'
  /** Will be accepted, but a person should look (wrong place, wrong state, extra box). */
  | 'warn'
  /** Will be refused. */
  | 'refuse';

export interface Precheck {
  level: PrecheckLevel;
  /** Plain words for the screen. Empty when level is `ok`. */
  message: string;
  /** Machine reason, for tests and for the list. */
  code?:
    BoxRefusalReason | 'unknown' | 'not_here' | 'extra' | 'already_received' | 'own_delivery_city';
}

const OK: Precheck = { level: 'ok', message: '' };

const REFUSAL_TEXT: Partial<Record<BoxRefusalReason, string>> = {
  unpaid: 'This box will be refused: not paid',
  active_hold: 'This box will be refused: on hold',
  blocking_hold: 'This box will be refused: on hold',
  illegal_state: 'This box will be refused: it cannot be scanned here now',
};

function balanceText(shipment: ShipmentRow | undefined): string {
  if (!shipment) return '';
  const currency = shipment.invoice.currency;
  if (!isCurrencyCode(currency)) return '';
  const balance = shipment.money.balanceMinor;
  return balance > 0 ? ` (${formatMoney(money(balance, currency))} to pay)` : '';
}

function holdText(box: BoxRow): string {
  const holds = activeHolds(box as Pick<Box, 'holds'>);
  const first = holds[0];
  if (!first) return '';
  return ` (${first.type.replace(/_/g, ' ')}${first.reason ? `: ${first.reason}` : ''})`;
}

/**
 * Loading: asks the one box state machine (`packages/domain`) what the server will say, using
 * the box and shipment the phone already has. A box the phone has never read is `ok`: the server
 * is the authority and shows its refusal when the command comes back.
 */
export function precheckLoad(args: {
  kind: 'trip' | 'container';
  carrierId: string;
  branchId: string;
  barcode: string;
  box: BoxRow | undefined;
  /** The box's shipment when it is unpaid or part paid. */
  unpaidShipment: ShipmentRow | undefined;
  at: string;
}): Precheck {
  const { kind, carrierId, branchId, barcode, box, unpaidShipment, at } = args;
  if (!box) return OK;
  const base = { barcode, occurredAt: at, branchId };
  const event: BoxEvent =
    kind === 'trip'
      ? { ...base, type: 'loadTrip', tripId: carrierId }
      : { ...base, type: 'loadContainer', containerId: carrierId };
  const outcome = applyBoxEvent(box as unknown as Box, event, {
    shipmentPaidInFull: kind === 'container' ? unpaidShipment === undefined : true,
  });
  switch (outcome.result) {
    case 'refused': {
      const text = REFUSAL_TEXT[outcome.reason] ?? `This box will be refused: ${outcome.reason}`;
      const detail =
        outcome.reason === 'unpaid'
          ? balanceText(unpaidShipment)
          : outcome.reason === 'active_hold' || outcome.reason === 'blocking_hold'
            ? holdText(box)
            : '';
      return { level: 'refuse', message: `${text}${detail}`, code: outcome.reason };
    }
    case 'exception':
      return {
        level: 'warn',
        message: `Will be accepted but flagged for review: ${outcome.exception.reason}`,
        code: 'not_here',
      };
    case 'duplicate':
      return { level: 'warn', message: 'Already loaded here', code: 'already_received' };
    case 'ok':
      return OK;
  }
}

/**
 * Receiving: is this box expected on the trip or container? An extra box is still received (the
 * physical fact wins), and the server opens an overage exception for it.
 */
export function precheckReceive(args: {
  expected: ReadonlyMap<string, OpsCarrierItem>;
  barcode: string;
}): Precheck {
  const item = args.expected.get(args.barcode);
  if (!item) {
    return {
      level: 'warn',
      message: 'Not on the list for this load: received as an extra and flagged',
      code: 'extra',
    };
  }
  if (item.receivedAt !== undefined || item.result === 'received') {
    return { level: 'warn', message: 'Already received', code: 'already_received' };
  }
  return OK;
}
